/**
 * <sd-cart-upsell-card> — one "Complete the look" card in the cart drawer.
 *
 * Adds its selected variant to the cart WITHOUT a page reload, and — critically
 * — without re-implementing the cart. Horizon's drawer does not repaint itself
 * when /cart/add.js is called directly: <cart-items-component> listens on
 * `document` for a CartLinesUpdateEvent and morphs the section from the
 * `sections` payload the add response carries. So this element follows exactly
 * the contract product-form.js uses:
 *
 *   1. create a deferred promise and dispatch CartLinesUpdateEvent immediately
 *      (optimistic UI elsewhere in the theme keys off this),
 *   2. POST to Theme.routes.cart_add_url asking for the drawer's section ids,
 *   3. resolve that promise with the fresh cart + the returned sections.
 *
 * Skipping step 1 or 3 leaves the drawer showing stale contents, which is the
 * failure this whole class exists to avoid.
 *
 * The variant <select> only swaps a value; no section re-render is needed
 * because every variant's id, price, and availability is already in the markup.
 *
 * Markup contract:
 *   <sd-cart-upsell-card>
 *     <select ref="variant">…<option value="ID" data-available="true">…</select>
 *     <button ref="add" on:click="/add">Add</button>
 *     <span ref="status" role="status"></span>
 *   </sd-cart-upsell-card>
 */

import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

// Long enough to read, short enough that the card returns to a usable state.
const ERROR_DISPLAY_MS = 5000;

class SdCartUpsellCard extends Component {
  requiredRefs = ['add'];

  #busy = false;
  #timer = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#timer);
  }

  /** The chosen variant id — the <select> when there is one, else the lone variant. */
  get #variantId() {
    return this.refs.variant?.value || this.dataset.variantId || '';
  }

  /**
   * Sync the button to the selected option. A variant that is sold out must not
   * be addable, and saying so on the button is clearer than failing on submit.
   */
  change() {
    const { add, variant } = this.refs;
    if (!variant) return;

    const option = variant.selectedOptions[0];
    const available = option?.dataset.available === 'true';

    add.disabled = !available;
    // dataset.labelSoldout/labelAdd come from Liquid so the strings stay
    // translatable and merchant-editable rather than hardcoded here.
    add.textContent = available ? this.dataset.labelAdd : this.dataset.labelSoldout;
  }

  /**
   * Opens this card's quick view. The dialog lives next to the card rather than
   * inside it — see sd-cart-quickview.js — so this hands off by id instead of
   * reaching into a child.
   */
  quickview() {
    const id = this.dataset.quickview;
    if (id) document.getElementById(id)?.open();
  }

  async add() {
    // Guard re-entry: a double-click would otherwise add the item twice.
    if (this.#busy) return;

    const id = this.#variantId;
    const { add, status } = this.refs;
    if (!id || add.disabled) return;

    this.#busy = true;
    add.disabled = true;
    this.dataset.loading = '';

    // The drawer is one section, but asking for every cart-items section id
    // keeps this correct if a page shows both a drawer and an inline cart.
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const body = new FormData();
    body.set('id', id);
    body.set('quantity', '1');
    if (sectionIds.length) body.set('sections', sectionIds.join(','));

    // Dispatched BEFORE the request so the rest of the theme can react
    // optimistically; the promise below is what carries the real result.
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: id, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        body,
      });
      const result = await response.json();

      // A 422 (e.g. inventory ran out between render and click) returns a body
      // with `status` set rather than throwing, so it must be checked here.
      if (result.status) throw new Error(result.description || result.message || 'Add to cart failed');

      const cart = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());

      // Resolving with `sections` is what actually repaints the drawer.
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'sd-cart-upsell',
          sections: result.sections,
          didError: false,
        },
      });

      this.#flash(this.dataset.labelAdded);
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Add to cart failed',
          code: 'INVALID',
        })
      );
      if (status) status.textContent = error?.message || '';
      this.#flash(this.dataset.labelError, true);
    }
  }

  /**
   * Momentary button feedback. The card may be morphed away by the drawer
   * re-render before this resolves, so every write is guarded by isConnected.
   */
  #flash(label, isError = false) {
    const { add, status } = this.refs;
    delete this.dataset.loading;
    add.textContent = label;
    if (!isError && status) status.textContent = label;

    clearTimeout(this.#timer);
    this.#timer = setTimeout(
      () => {
        this.#busy = false;
        if (!this.isConnected) return;
        add.textContent = this.dataset.labelAdd;
        add.disabled = false;
        if (status) status.textContent = '';
      },
      isError ? ERROR_DISPLAY_MS : 1400
    );
  }
}

if (!customElements.get('sd-cart-upsell-card')) {
  customElements.define('sd-cart-upsell-card', SdCartUpsellCard);
}
